import { Skeleton } from './ui/skeleton';
import PageLayout from './PageLayout';

/**
 * Skeletons mostrados por el Suspense de cada ruta mientras se descarga el
 * chunk lazy de la página. Reutilizan PageLayout (mismo padding + breadcrumb)
 * para que no haya salto visual cuando llega el contenido real.
 */

const ProductCardSkeleton = () => (
  <div className="overflow-hidden rounded-xl border border-border">
    <Skeleton className="h-40 w-full rounded-none sm:h-44" />
    <div className="flex flex-col gap-2 p-4">
      <Skeleton className="h-3 w-1/3" />
      <Skeleton className="h-4 w-3/4" />
      <Skeleton className="h-3 w-full" />
    </div>
  </div>
);

export const HomeSkeleton = () => (
  <div className="flex flex-col">
    <Skeleton className="h-[115vh] sm:h-screen w-full rounded-none" />
    {[0, 1].map(i => (
      <div key={i} className="w-full flex items-center justify-center min-h-[50vh] py-10">
        <div className="w-full max-w-screen-lg grid grid-cols-1 md:grid-cols-2 gap-6 px-4">
          <Skeleton className="h-56 w-full" />
          <div className="flex flex-col justify-center gap-3">
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-8 w-3/4" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-5/6" />
          </div>
        </div>
      </div>
    ))}
  </div>
);

export const ProductsSkeleton = () => (
  <PageLayout maxWidth="xl">
    <div className="flex w-full flex-col gap-6 sm:gap-8">
      <div className="flex flex-col gap-2">
        <Skeleton className="h-3 w-20" />
        <Skeleton className="h-9 w-56" />
      </div>
      <div className="grid grid-cols-1 gap-8 lg:grid-cols-[240px_1fr]">
        <div className="hidden flex-col gap-3 pt-2 lg:flex">
          {Array.from({ length: 6 }, (_, i) => (
            <Skeleton key={i} className="h-5 w-full" />
          ))}
        </div>
        <div className="flex flex-col gap-5 w-full">
          <div className="flex items-center justify-between gap-3">
            <Skeleton className="h-5 w-40" />
            <Skeleton className="h-10 w-28" />
          </div>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 9 }, (_, i) => (
              <ProductCardSkeleton key={i} />
            ))}
          </div>
        </div>
      </div>
    </div>
  </PageLayout>
);

export const ProductDetailSkeleton = () => (
  <PageLayout maxWidth="xl">
    <div className="w-full">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
        <Skeleton className="aspect-square w-full" />
        <div className="flex flex-col gap-3">
          <Skeleton className="h-8 w-3/4" />
          <Skeleton className="h-4 w-1/3" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-5/6" />
          <div className="flex gap-3 mt-4">
            <Skeleton className="h-11 w-40" />
            <Skeleton className="h-11 w-40" />
          </div>
        </div>
      </div>
      <div className="flex flex-col gap-3">
        <Skeleton className="h-6 w-48" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-2/3" />
      </div>
    </div>
  </PageLayout>
);

export const HistorySkeleton = () => (
  <div className="flex flex-col">
    <Skeleton className="h-72 w-full rounded-none sm:h-80" />
    <div className="mx-auto grid w-full max-w-screen-xl grid-cols-1 gap-6 px-4 py-12 sm:grid-cols-3 sm:px-6 lg:px-10">
      {[0, 1, 2].map(i => (
        <div key={i} className="flex flex-col gap-2">
          <Skeleton className="h-9 w-20" />
          <Skeleton className="h-4 w-32" />
        </div>
      ))}
    </div>
    <div className="mx-auto flex w-full max-w-screen-xl flex-col gap-6 px-4 pb-16 sm:px-6 lg:px-10">
      {Array.from({ length: 4 }, (_, i) => (
        <Skeleton key={i} className="h-20 w-full" />
      ))}
    </div>
  </div>
);

export const ArticleSkeleton = () => (
  <PageLayout>
    <div className="p-5 sm:p-10 bg-[#f6f6f6] w-full">
      <Skeleton className="h-7 w-1/3" />
      <hr className="my-2 border-border" />
      <div className="flex flex-col gap-3 mt-4">
        {Array.from({ length: 8 }, (_, i) => (
          <Skeleton key={i} className={`h-4 ${i % 3 === 2 ? 'w-2/3' : 'w-full'}`} />
        ))}
      </div>
    </div>
  </PageLayout>
);

export const ContactSkeleton = () => (
  <PageLayout maxWidth="xl">
    <div className="flex w-full flex-col gap-6 pb-16 sm:gap-8">
      <div className="flex flex-col gap-2">
        <Skeleton className="h-3 w-20" />
        <Skeleton className="h-9 w-64" />
      </div>
      <div className="grid grid-cols-1 gap-8 lg:grid-cols-[1fr_1.1fr]">
        <div className="flex flex-col gap-3.5">
          {Array.from({ length: 5 }, (_, i) => (
            <Skeleton key={i} className="h-16 w-full rounded-xl" />
          ))}
        </div>
        <Skeleton className="min-h-[380px] w-full rounded-xl" />
      </div>
    </div>
  </PageLayout>
);

export const ConsultSkeleton = () => (
  <PageLayout maxWidth="xl">
    <div className="flex w-full flex-col gap-6 pb-16 sm:gap-8">
      <div className="grid grid-cols-1 gap-8 lg:grid-cols-[1.4fr_0.9fr]">
        <div className="flex flex-col gap-4">
          <Skeleton className="h-3 w-24" />
          <Skeleton className="h-9 w-64" />
          <div className="mt-2 grid grid-cols-1 gap-4 sm:grid-cols-2">
            {Array.from({ length: 4 }, (_, i) => (
              <Skeleton key={i} className="h-11 w-full" />
            ))}
          </div>
          <Skeleton className="h-28 w-full" />
          <Skeleton className="h-12 w-40" />
        </div>
        <Skeleton className="h-72 w-full rounded-xl" />
      </div>
    </div>
  </PageLayout>
);

/** Fallback genérico (404 y cualquier ruta sin skeleton propio). */
export const GenericPageSkeleton = () => (
  <PageLayout>
    <div className="flex flex-col items-center gap-4 w-full mt-8">
      <Skeleton className="h-40 w-40 rounded-full" />
      <Skeleton className="h-5 w-64" />
      <Skeleton className="h-10 w-40" />
    </div>
  </PageLayout>
);
